import {
  DragDropContext,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import { Inbox } from "lucide-react";
import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

import "@/styles/mobile-glass.css";

interface KanbanBoardProps {
  onDragEnd: OnDragEndResponder;
  children: ReactNode;
  className?: string;
}

/**
 * Horizontal kanban shared by the deals and team-tasks boards (styles in
 * `src/styles/mobile-glass.css`). On phones the columns snap one by one with
 * the next one peeking; snapping is suspended while a card is dragged so it
 * does not fight the library's auto-scroll.
 */
export const KanbanBoard = ({
  onDragEnd,
  children,
  className,
}: KanbanBoardProps) => {
  const [isDragging, setIsDragging] = useState(false);
  return (
    <DragDropContext
      onDragStart={() => setIsDragging(true)}
      onDragEnd={(result, provided) => {
        setIsDragging(false);
        onDragEnd(result, provided);
      }}
    >
      <div
        className={cn("kanban-board", className)}
        data-dragging={isDragging ? "true" : undefined}
      >
        {children}
      </div>
    </DragDropContext>
  );
};

interface KanbanColumnProps {
  title: ReactNode;
  count: number;
  /** Secondary figure under the title (e.g. the column total). */
  meta?: ReactNode;
  /** Status dot colour. */
  color?: string;
  /** Trailing header control (e.g. a "+" link). */
  action?: ReactNode;
  children: ReactNode;
}

export const KanbanColumn = ({
  title,
  count,
  meta,
  color,
  action,
  children,
}: KanbanColumnProps) => (
  <section className="kanban-col">
    <header className="kanban-col__head">
      <div className="kanban-col__heading">
        <h3 className="kanban-col__title">
          {color ? (
            <span
              aria-hidden
              className="kanban-col__dot"
              style={{ backgroundColor: color }}
            />
          ) : null}
          <span className="truncate">{title}</span>
          <span className="kanban-col__count">{count}</span>
        </h3>
        {meta ? <p className="kanban-col__meta">{meta}</p> : null}
      </div>
      {action}
    </header>
    {children}
  </section>
);

interface KanbanDropZoneProps {
  droppableId: string;
  isEmpty: boolean;
  emptyLabel: string;
  children: ReactNode;
}

/** Droppable card list of a column, with a soft empty state. */
export const KanbanDropZone = ({
  droppableId,
  isEmpty,
  emptyLabel,
  children,
}: KanbanDropZoneProps) => (
  <Droppable droppableId={droppableId}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        className="kanban-col__body"
        data-over={snapshot.isDraggingOver ? "true" : undefined}
      >
        {children}
        {provided.placeholder}
        {isEmpty && !snapshot.isDraggingOver ? (
          <div className="kanban-empty">
            <Inbox aria-hidden className="size-5" strokeWidth={1.75} />
            <span>{emptyLabel}</span>
          </div>
        ) : null}
      </div>
    )}
  </Droppable>
);
